import { useState } from 'react';
import { Button, Icon } from '../components';
import { RATING_SCALE, type AnswerKind } from './SurveyEditor';

export interface SurveySheetQuestion {
  key: string;
  text: string;
  type: AnswerKind;
  required: boolean;
  options: { key: string; text: string }[];
  scaleMinLabel: string;
  scaleMaxLabel: string;
}

/** The runner app's prompt sheet at phone width, one question per page. The app's fonts differ. */
export function SurveySheet({
  title,
  questions,
}: {
  title: string;
  questions: SurveySheetQuestion[];
}) {
  const [picked, setPicked] = useState(0);
  const last = Math.max(0, questions.length - 1);
  const index = Math.min(picked, last);
  const question = questions[index];
  const count = questions.length;
  const isLast = index === last;
  const word = count === 1 ? '1 quick question' : `${count} quick questions`;

  return (
    <div className="rq-app-preview">
      <p className="rq-sr">
        A picture of the survey as the app shows it. Use the buttons below to step through the
        questions.
      </p>
      <div className="rq-app-sheet" aria-hidden="true" inert>
        <span className="rq-app-sheet-grip" />
        <div className="rq-app-sheet-head">
          <div className="rq-app-sheet-heading">
            <span className="rq-app-sheet-title">{title || ' '}</span>
            <span className="rq-app-sheet-meta">
              <span>{word}</span>
              {count > 1 ? <span>{`${index + 1} of ${count}`}</span> : null}
            </span>
          </div>
          <Icon name="x" size={20} />
        </div>

        {question ? (
          <div className="rq-app-sheet-q">
            <p className="rq-app-sheet-text">
              {question.text || <span className="rq-missing">No wording yet</span>}
              {question.required ? <span className="rq-app-sheet-req"> *</span> : null}
            </p>
            <Answer question={question} />
          </div>
        ) : null}

        <div className="rq-app-sheet-foot">
          {index > 0 ? <span className="rq-app-sheet-back">Back</span> : <span />}
          {question && !question.required ? (
            <span className="rq-app-sheet-skip">Skip</span>
          ) : (
            <span className="rq-app-sheet-submit is-off">{isLast ? 'Submit' : 'Next'}</span>
          )}
        </div>
      </div>

      <div className="rq-app-pager">
        <Button
          size="sm"
          variant="quiet"
          iconOnly
          icon="chevron-left"
          label="Previous question"
          aria-disabled={index === 0 ? 'true' : undefined}
          onClick={() => setPicked((i) => Math.max(0, Math.min(i, last) - 1))}
        />
        <span className="rq-app-pager-page" aria-live="polite">
          {count ? `Question ${index + 1} of ${count}` : 'No questions'}
        </span>
        <Button
          size="sm"
          variant="quiet"
          iconOnly
          icon="chevron-right"
          label="Next question"
          aria-disabled={index >= last ? 'true' : undefined}
          onClick={() => setPicked((i) => Math.min(last, i + 1))}
        />
      </div>
    </div>
  );
}

function Answer({ question }: { question: SurveySheetQuestion }) {
  if (question.type === 'free-text') {
    return (
      <span className="rq-app-sheet-input">
        <span>Type your answer here…</span>
        <span className="rq-app-sheet-count">0/2000</span>
      </span>
    );
  }
  if (question.type === 'rating') {
    return (
      <div className="rq-app-scale">
        <div className="rq-app-scale-row">
          {RATING_SCALE.map((n) => (
            <span key={n} className="rq-app-scale-button">
              {n}
            </span>
          ))}
        </div>
        <div className="rq-app-scale-labels">
          <span>{question.scaleMinLabel.trim()}</span>
          <span>{question.scaleMaxLabel.trim()}</span>
        </div>
      </div>
    );
  }
  return (
    <ul className="rq-app-sheet-options">
      {question.options.map((o) => (
        <li key={o.key}>
          <span
            className={
              question.type === 'single-choice' ? 'rq-app-sheet-mark' : 'rq-app-sheet-mark is-box'
            }
          />
          {o.text || ' '}
        </li>
      ))}
    </ul>
  );
}
